import React, { useRef, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

interface VideoPlayerModalProps {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
  title?: string;
  width?: string | number;
  height?: string | number;
  autoPlay?: boolean;
  duration?: number; // Start time in seconds
  containerStyle?: React.CSSProperties;
}

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({
  open,
  onClose,
  videoUrl,
  title = "Video Player",
  width = "80%",
  height = "80vh",
  autoPlay = false,
  duration = 0,
  containerStyle,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    // Reset video when modal opens
    if (open && videoRef.current) {
      videoRef.current.load();
      if (autoPlay) {
        videoRef.current.play().catch((error) => {
          console.error("Auto-play failed:", error);
        });
      }
    }
  }, [open, videoUrl, autoPlay]);

  useEffect(() => {
    // Set video start time when duration prop is provided
    if (open && videoRef.current && duration > 0) {
      const handleLoadedMetadata = () => {
        if (videoRef.current) {
          videoRef.current.currentTime = duration;
        }
      };

      const video = videoRef.current;
      video.addEventListener("loadedmetadata", handleLoadedMetadata);

      // If metadata is already loaded, set the time immediately
      if (video.readyState >= 1) {
        video.currentTime = duration;
      }

      return () => {
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }
  }, [open, duration, videoUrl]);

  const handleToggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable fullscreen:", err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Background Overlay with higher z-index */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.85)",
          zIndex: 1400,
          backdropFilter: "blur(8px)",
        }}
        onClick={handleBackgroundClick}
      />

      {/* Video Player Container */}
      <Box
        ref={containerRef}
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: width,
          maxWidth: "1600px",
          height: height,
          maxHeight: "90vh",
          minHeight: "400px",
          zIndex: 1401,
          backgroundColor: "#000000",
          borderRadius: isFullscreen ? 0 : "12px",
          boxShadow: "0 24px 48px rgba(0, 0, 0, 0.5)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          ...containerStyle,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
            color: "#ffffff",
            borderRadius: isFullscreen ? 0 : "12px 12px 0 0",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              letterSpacing: 0.5,
              textShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {title}
          </Typography>
          
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={handleToggleFullscreen}
              size="small"
              sx={{
                color: "#ffffff",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  transform: "scale(1.1)",
                },
              }}
            >
              {isFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </IconButton>
            
            <IconButton
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.pause();
                }
                onClose();
              }}
              size="small"
              sx={{
                color: "#ffffff",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Video Player */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#000000",
            padding: 0,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <video
            ref={videoRef}
            controls
            controlsList="nodownload"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            <source src={videoUrl} type="video/x-matroska" />
            <source src={videoUrl} type="video/webm" />
            Your browser does not support the video tag.
          </video>
        </Box>
      </Box>
    </>
  );
};

export default VideoPlayerModal;
